import React, {
  useEffect,
  useMemo,
  useRef,
  useState,
  useCallback,
} from "react";
import "./Checkout.scss";
import { theme, Layout, Button, Typography, notification } from "antd";
import { ArrowLeftOutlined } from "@ant-design/icons";
import { BillingForm, OrderDetails, CartOrderDetails } from "./components";
import useWindowWidth from "../../hooks/useWindowWidth";
import { useNavigate } from "react-router";
import { calculateOrder } from "../../features/order/orderActions";
import { useDispatch, useSelector } from "react-redux";
import {
  resetPatientError,
  addPatient,
  forgetGuestPatient,
} from "../../features/patient/patientSlice";
import {
  getPatientDetails,
  createPatient,
  getPatientShippingAddresses,
} from "../../features/patient/patientActions";
import Utils from "../../utils";

const { Content } = Layout;

export default function Checkout() {
  const { useToken } = theme;
  const { token } = useToken();
  const [api, contextHolder] = notification.useNotification();
  const openNotificationWithIcon = useCallback(
    (type, title, text) => {
      api[type]({
        title: title,
        description: Utils.getErrorText(text),
        placement: "top",
      });
    },
    [api]
  );
  const formRef = useRef(null);
  const hasRunRef = useRef(false);
  const { width, isMobile } = useWindowWidth();
  const {
    isAuthenticated,
    info: { patient: authenticatedPatient },
  } = useSelector((state) => state?.auth);
  const {
    error,
    unAuthenticatedPatientId,
    loading: patientDetailsLoading,
  } = useSelector((state) => state?.patient);
  const { items, loading: cardLoading } = useSelector((state) => state?.card);
  const navigate = useNavigate();
  const haveCartCredentials = isAuthenticated ? true : unAuthenticatedPatientId;
  const dispatch = useDispatch();
  const [orderCalculation, setOrderCalculation] = useState({});
  const [orderLoading, setOrderLoading] = useState(false);
  const [selectedPromoCode, setSelectedPromoCode] = useState(undefined);

  // [CANA-CALC-PERF] The calculate request is keyed on its CONTENT, not on
  // the identity of `items`: during the 2026-09-28 incident one checkout
  // re-sent a byte-identical ~10KB calculate body every 1-2s for an hour
  // (something upstream kept handing `items` a new reference), which a
  // debounce alone can't stop when re-renders are further apart than its
  // window. With the key, an identical cart never re-fires; only a real
  // change (variant, quantity or promo code) does, debounced so a burst of
  // quantity clicks costs one call, and `ignore` drops a stale response.
  const calcKey = useMemo(() => {
    if (!items?.length) return "";
    return JSON.stringify({
      order_items: items.map((item) => ({
        variant_id: item?.variant?.id,
        quantity: item?.quantity,
      })),
      promo_code: selectedPromoCode ? selectedPromoCode?.value : undefined,
    });
  }, [items, selectedPromoCode]);

  useEffect(() => {
    if (!calcKey) {
      setOrderCalculation({});
      return;
    }
    let ignore = false;
    const timeoutId = setTimeout(() => {
      dispatch(calculateOrder(JSON.parse(calcKey))).then((data) => {
        if (ignore) return;
        if (data?.type === "calculateOrder/fulfilled") {
          if (data?.payload) {
            setOrderCalculation(data?.payload);
          }
        }
      });
    }, 500);
    return () => {
      ignore = true;
      clearTimeout(timeoutId);
    };
  }, [calcKey, dispatch]);

  useEffect(() => {
    if (!isAuthenticated && haveCartCredentials && unAuthenticatedPatientId) {
      Promise.all([
        dispatch(getPatientDetails({ id: unAuthenticatedPatientId })),
        dispatch(getPatientShippingAddresses({ id: unAuthenticatedPatientId })),
      ]).then((results) => {
        // The guest session only reads guest records (no shop login), and
        // only with the guest_token this browser got when it created the
        // record. A guest id cached here whose record has since got a login
        // (claimed at signup, or a patient merge moved one onto it), or whose
        // token is missing (saved before tokens existed) or expired, answers
        // 404: forget it, token included, so the guest enters the details
        // again instead of being left with a cart that can never be ordered.
        if (results?.some((res) => res?.meta?.status === 404)) {
          dispatch(forgetGuestPatient(unAuthenticatedPatientId));
        }
      });
    } else if (
      isAuthenticated &&
      haveCartCredentials &&
      authenticatedPatient?.id
    ) {
      dispatch(
        getPatientDetails({
          id: authenticatedPatient?.id ? authenticatedPatient?.id : "",
        })
      );
      dispatch(
        getPatientShippingAddresses({
          id: authenticatedPatient?.id ? authenticatedPatient?.id : "",
        })
      );
    }
  }, [
    dispatch,
    isAuthenticated,
    haveCartCredentials,
    unAuthenticatedPatientId,
    authenticatedPatient?.id,
  ]);

  const addCartDetails = (params, reGenerateCaptcha) => {
    dispatch(createPatient(params))?.then((res) => {
      if (res?.type === "create_patient/rejected") {
        let error = res?.payload;
        const keys = Object.keys(error);
        const lastIndex = keys.length - 1;
        let currentIndex = 0;

        for (let key in error) {
          if (key === "non_field_errors") {
            if (error[key]) {
              if (error[key][0]?.includes("captcha")) {
                formRef?.current?.setFields([
                  {
                    name: "captcha_value",
                    errors: [error[key]],
                  },
                ]);
                if (currentIndex === lastIndex) {
                  Utils?.scrollIntoViewByClassName(".cartForm-cont");
                }
              }
            }
          } else {
            formRef?.current?.setFields([
              {
                name: key,
                errors: [error[key]],
              },
            ]);
            if (currentIndex === lastIndex) {
              Utils?.scrollIntoViewByClassName(".cartForm-cont");
            }
          }
          currentIndex++;
        }
        if (error?.detail && error && typeof error?.detail === "string") {
          openNotificationWithIcon("error", "Error", error?.detail);
          dispatch(resetPatientError());
        }
        if (error?.Error && error && typeof error?.Error === "string") {
          openNotificationWithIcon("error", "Error", error?.Error);
          dispatch(resetPatientError());
        }
        if (typeof reGenerateCaptcha === "function") {
          reGenerateCaptcha();
        }
      } else if (res?.type === "create_patient/fulfilled") {
        // A new customer answers 201; a phone already on file may answer 200
        // already_exists with that customer's record, which keeps its own
        // name. Either way the order carries the name typed here as
        // recipient_name, so it is kept alongside the id. So is guest_token:
        // the backend opens this guest record to requests that send it back.
        const typedName = [params?.first_name, params?.last_name]
          .map((part) => (typeof part === "string" ? part.trim() : ""))
          .filter(Boolean)
          .join(" ");
        dispatch(
          addPatient({
            id: res?.payload?.id,
            phone_number: res?.payload?.phone_number,
            recipient_name: res?.payload?.recipient_name || typedName,
            guest_token: res?.payload?.guest_token,
          })
        );
        formRef?.current?.resetFields();
      }
    });
  };

  useEffect(() => {
    if (hasRunRef.current) return;
    if (!haveCartCredentials) {
      if (error) {
        dispatch(resetPatientError());
      }
    }
    hasRunRef.current = true;
  }, [dispatch, error, haveCartCredentials]);

  return (
    <>
      {contextHolder}
      <Content
        className="checkout-cont"
        style={{
          backgroundColor: token?.Layout?.contentBg,
        }}>
        <div className="checkout-cont__header">
          <Button
            type="text"
            size="small"
            icon={
              <ArrowLeftOutlined
                style={{
                  fontSize: "24px",
                  color: token?.icons?.whiteIcon,
                }}
              />
            }
            onClick={() => {
              if (haveCartCredentials) {
                navigate(-1);
              } else {
                navigate(-1);
                formRef.current.resetFields();
                if (error) {
                  dispatch(resetPatientError());
                }
              }
            }}
          />
          <Typography.Text
            className="checkout-cont__header__title"
            style={{
              color: token?.Typography?.mainColorText,
            }}>
            {haveCartCredentials ? "Cart" : "Shipping Details"}
          </Typography.Text>
        </div>

        <div className="checkout-cont__prodDetails">
          <OrderDetails
            orderCalculation={orderCalculation}
            token={token}
            isMobile={isMobile}
            items={items}
            width={width}
            orderLoading={orderLoading || (isAuthenticated && cardLoading)}
          />
          <CartOrderDetails
            orderCalculation={orderCalculation}
            token={token}
            isMobile={isMobile}
            openNotificationWithIcon={openNotificationWithIcon}
            haveCartCredentials={haveCartCredentials}
            formRef={formRef}
            orderLoading={orderLoading}
            setOrderLoading={setOrderLoading}
            setSelectedPromoCode={setSelectedPromoCode}
            selectedPromoCode={selectedPromoCode}
            unAuthenticatedPatientId={unAuthenticatedPatientId}
          />
        </div>
        {!isAuthenticated && !unAuthenticatedPatientId ? (
          <BillingForm
            formRef={formRef}
            addCartDetails={addCartDetails}
            isMobile={isMobile}
            loading={patientDetailsLoading}
            error={error}
            dispatch={dispatch}
            resetPatientError={resetPatientError}
          />
        ) : null}
      </Content>
    </>
  );
}
